/** Browser-safe metadata. Add a model here; provider execution stays server-only. */
export type EffortOption = { readonly id: string; readonly label: string };
export type ModelDefinition = {
  readonly id: string;
  readonly label: string;
  readonly provider: string;
  readonly upstreamId: string;
  readonly efforts: readonly EffortOption[];
  /** null means the model has no selectable reasoning setting. */
  readonly defaultEffort: string | null;
  readonly default?: boolean;
};

/** Validate metadata at startup/build import, before it can become a selectable model. */
export function defineModelCatalog<const T extends readonly ModelDefinition[]>(models: T): T {
  const ids = new Set<string>();
  for (const model of models) {
    if (
      !model.id.trim() ||
      !model.label.trim() ||
      !model.provider.trim() ||
      !model.upstreamId.trim()
    ) {
      throw new Error('Model metadata must contain non-empty IDs, provider and label');
    }
    if (ids.has(model.id)) throw new Error(`Duplicate model ID: ${model.id}`);
    ids.add(model.id);
    const efforts = new Set<string>();
    for (const option of model.efforts) {
      if (
        !option.id.trim() ||
        option.id === 'auto' ||
        !option.label.trim() ||
        efforts.has(option.id)
      ) {
        throw new Error(`Invalid reasoning option for ${model.id}: ${option.id}`);
      }
      efforts.add(option.id);
    }
    if (
      (efforts.size === 0 && model.defaultEffort !== null) ||
      (efforts.size > 0 && (model.defaultEffort === null || !efforts.has(model.defaultEffort)))
    ) {
      throw new Error(`Invalid default reasoning level for ${model.id}`);
    }
  }
  if (models.filter((model) => model.default).length !== 1) {
    throw new Error('Model catalog must have exactly one default model');
  }
  return models;
}

// Damir, 2026-10-09: the Claude subscription is gone; chats are answered
// by the Notion custom agent «Maclaren» through the Notion Agent API.
// upstreamId is the agent id. Its model is picked in the agent settings
// (Notion has no per-message model switch); add a catalog entry per agent
// to offer more than one.
const NO_EFFORTS: readonly EffortOption[] = [];

export const MODEL_CATALOG = defineModelCatalog([
  {
    id: 'maclaren',
    label: 'Maclaren',
    provider: 'notion',
    upstreamId: '1014257f-e2d1-484a-988e-e4d712851dd7',
    efforts: NO_EFFORTS,
    defaultEffort: null,
    default: true,
  },
]);

export type ModelId = (typeof MODEL_CATALOG)[number]['id'];
/** `auto` is accepted for old clients and stored preferences, never a fake UI choice. */
export type Effort = (typeof MODEL_CATALOG)[number]['efforts'][number]['id'] | 'auto';
export const MODEL_IDS = MODEL_CATALOG.map((model) => model.id);
export const DEFAULT_MODEL_ID: ModelId =
  MODEL_CATALOG.find((model: ModelDefinition) => model.default)?.id ?? MODEL_CATALOG[0].id;

export function isValidModelId(value: unknown): value is ModelId {
  return typeof value === 'string' && MODEL_CATALOG.some((model) => model.id === value);
}

export function getModelDefinition(id: ModelId): (typeof MODEL_CATALOG)[number] {
  const model = MODEL_CATALOG.find((entry) => entry.id === id);
  if (!model) throw new Error(`Unknown model: ${id}`);
  return model;
}

export function isEffort(value: unknown): value is Effort {
  return (
    value === 'auto' ||
    MODEL_CATALOG.some((model) => model.efforts.some((option) => option.id === value))
  );
}

export function effortOptionsForModel(model: ModelId): readonly { id: Effort; label: string }[] {
  return getModelDefinition(model).efforts;
}

export function effortsForModel(model: ModelId): readonly Effort[] {
  return effortOptionsForModel(model).map((option) => option.id);
}

export function defaultEffortForModel(model: ModelId): Effort {
  return getModelDefinition(model).defaultEffort ?? 'auto';
}

export function clampEffortForModel(effort: Effort, model: ModelId): Effort {
  return effortsForModel(model).includes(effort) ? effort : defaultEffortForModel(model);
}

/** Convert legacy/default selection to an actual provider level, or null for no reasoning. */
export function resolveModelEffort(model: ModelDefinition, effort?: string): string | null {
  if (effort === undefined || effort === 'auto') return model.defaultEffort;
  if (!model.efforts.some((option) => option.id === effort)) {
    throw new Error(`Reasoning level ${effort} is not available for ${model.label}`);
  }
  return effort;
}

export function effortAllowedForModel(
  model: ModelId | undefined,
  effort: Effort | undefined,
): boolean {
  if (effort === undefined || effort === 'auto') return true;
  return effortsForModel(model ?? DEFAULT_MODEL_ID).includes(effort);
}
